/**
 * Redux action creators for the Projects feature.
 * Handles all project CRUD, resource linking, and collaborator operations.
 */

import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import apis from '../../../utils/apis';
import { showFeedbackModal } from '../UserFeedbackActions';
import { RootState } from '../../index';

// Action type constants
export const PROJECTS_LOADING = 'PROJECTS_LOADING';
export const PROJECTS_FETCH_LIST = 'PROJECTS_FETCH_LIST';
export const PROJECTS_APPEND_LIST = 'PROJECTS_APPEND_LIST';
export const PROJECTS_SET_DETAIL = 'PROJECTS_SET_DETAIL';
export const PROJECTS_LOADING_DETAIL = 'PROJECTS_LOADING_DETAIL';
export const PROJECTS_UPDATE_IN_LIST = 'PROJECTS_UPDATE_IN_LIST';
export const PROJECTS_REMOVE_FROM_LIST = 'PROJECTS_REMOVE_FROM_LIST';
export const PROJECTS_SET_RESOURCES = 'PROJECTS_SET_RESOURCES';
export const PROJECTS_LOADING_RESOURCES = 'PROJECTS_LOADING_RESOURCES';
export const PROJECTS_SET_COLLABORATORS = 'PROJECTS_SET_COLLABORATORS';
export const PROJECTS_SET_MILESTONES = 'PROJECTS_SET_MILESTONES';
export const PROJECTS_LOADING_MILESTONES = 'PROJECTS_LOADING_MILESTONES';
export const PROJECTS_SET_DASHBOARD = 'PROJECTS_SET_DASHBOARD';
export const PROJECTS_LOADING_DASHBOARD = 'PROJECTS_LOADING_DASHBOARD';
export const PROJECTS_SET_FEED = 'PROJECTS_SET_FEED';
export const PROJECTS_APPEND_FEED = 'PROJECTS_APPEND_FEED';
export const PROJECTS_LOADING_FEED = 'PROJECTS_LOADING_FEED';
export const PROJECTS_ERROR = 'PROJECTS_ERROR';

type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>;

// Helper to dispatch feedback
const feedback = (dispatch: AppDispatch, modalType: 'success' | 'error', message: string) => {
  dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 3000 }));
};

// ─── List Projects ─────────────────────────────────────────────

interface FetchProjectsParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: string;
  search_term?: string;
  status?: string;
}

export const fetchProjects = (params: FetchProjectsParams = {}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING, payload: true });

      const response = await apis.get('/projects', { params });

      const actionType = (params.page && params.page > 1) ? PROJECTS_APPEND_LIST : PROJECTS_FETCH_LIST;
      dispatch({
        type: actionType,
        payload: {
          items: response.data.items || [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          total_pages: response.data.total_pages || 0,
        },
      });

      return response.data;
    } catch (error: any) {
      dispatch({ type: PROJECTS_ERROR, payload: error.message });
      feedback(dispatch, 'error', 'Failed to load projects');
      return { items: [], total: 0, page: 1, total_pages: 0 };
    } finally {
      dispatch({ type: PROJECTS_LOADING, payload: false });
    }
  };
};

// ─── Get Project Detail ────────────────────────────────────────

// Sentinel returned by fetchProjectDetail when the backend denies access
// (403) or reports the project as missing (404). The caller (ProjectDetail
// page) uses this to redirect away. Treating 403 and 404 identically avoids
// leaking project existence to non-collaborators.
export const PROJECT_DETAIL_FORBIDDEN = { forbidden: true } as const;

export const fetchProjectDetail = (projectUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING_DETAIL, payload: true });

      const response = await apis.get(`/projects/${projectUuid}`);
      dispatch({ type: PROJECTS_SET_DETAIL, payload: response.data });

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        dispatch({ type: PROJECTS_ERROR, payload: 'forbidden' });
        feedback(dispatch, 'error', 'You don\'t have access to this project');
        return PROJECT_DETAIL_FORBIDDEN;
      }
      dispatch({ type: PROJECTS_ERROR, payload: error.message });
      feedback(dispatch, 'error', 'Failed to load project details');
      return null;
    } finally {
      dispatch({ type: PROJECTS_LOADING_DETAIL, payload: false });
    }
  };
};

// ─── Create Project ────────────────────────────────────────────

export const createProject = (data: { title: string; description?: string; status?: string; priority?: string; due_date?: string; shared_with?: any[] }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post('/projects', data);
      feedback(dispatch, 'success', 'Project created successfully');
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to create project');
      return null;
    }
  };
};

// ─── Update Project ────────────────────────────────────────────

export const updateProject = (projectUuid: string, data: { title?: string; description?: string; status?: string; priority?: string; due_date?: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/edit`, data);
      dispatch({ type: PROJECTS_UPDATE_IN_LIST, payload: { uuid: projectUuid, ...response.data } });
      feedback(dispatch, 'success', 'Project updated successfully');
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to update project');
      return null;
    }
  };
};

// ─── Delete Project ────────────────────────────────────────────

export const deleteProject = (projectUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/delete`);
      dispatch({ type: PROJECTS_REMOVE_FROM_LIST, payload: projectUuid });
      feedback(dispatch, 'success', 'Project deleted successfully');
      return true;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to delete project');
      return false;
    }
  };
};

// ─── Resource Linking ──────────────────────────────────────────

export const fetchProjectResources = (projectUuid: string, resourceType?: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING_RESOURCES, payload: true });

      const params: any = {};
      if (resourceType) params.resource_type = resourceType;

      const response = await apis.get(`/projects/${projectUuid}/resources`, { params });
      dispatch({ type: PROJECTS_SET_RESOURCES, payload: response.data });

      return response.data;
    } catch (error: any) {
      dispatch({ type: PROJECTS_ERROR, payload: error.message });
      return null;
    } finally {
      dispatch({ type: PROJECTS_LOADING_RESOURCES, payload: false });
    }
  };
};

// Format a backend resource type token for display:
//   "review_session" → "Review"
//   "data_source"    → "Data Source"
//   "research_chat"  → "Research Chat"
const formatResourceTypeLabel = (raw: string): string => {
  if (!raw) return 'Resource';
  const lower = String(raw).trim().toLowerCase();
  if (lower === 'review_session') return 'Review';
  return lower
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const addResourceToProject = (projectUuid: string, resourceType: string, resourceUuid: string, notes?: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/resources`, {
        resource_type: resourceType,
        resource_uuid: resourceUuid,
        notes,
      });
      feedback(dispatch, 'success', `${formatResourceTypeLabel(resourceType)} linked to project`);
      dispatch(fetchProjectResources(projectUuid));
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', `Failed to link ${formatResourceTypeLabel(resourceType).toLowerCase()} to project`);
      return null;
    }
  };
};

export const removeResourceFromProject = (projectUuid: string, resourceType: string, resourceUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/resources/remove`, {
        resource_type: resourceType,
        resource_uuid: resourceUuid,
      });
      feedback(dispatch, 'success', `${formatResourceTypeLabel(resourceType)} unlinked from project`);
      dispatch(fetchProjectResources(projectUuid));
      return true;
    } catch (error: any) {
      feedback(dispatch, 'error', `Failed to unlink ${formatResourceTypeLabel(resourceType).toLowerCase()}`);
      return false;
    }
  };
};

// ─── Collaborators ─────────────────────────────────────────────

export const fetchProjectCollaborators = (projectUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.get(`/projects/${projectUuid}/collaborators`);
      dispatch({ type: PROJECTS_SET_COLLABORATORS, payload: response.data.collaborators || [] });
      return response.data.collaborators;
    } catch (error: any) {
      return [];
    }
  };
};

export const addProjectCollaborator = (projectUuid: string, collaboratorIdOrUuid: number | string, access: string = 'view') => {
  return async (dispatch: AppDispatch) => {
    try {
      // Accept either a numeric user_id or a user UUID. The collaborator panel
      // passes a UUID (collaborator_uuid) — converting that with Number(...)
      // produces NaN and the backend rejects the payload.
      const raw = collaboratorIdOrUuid;
      const asString = String(raw ?? '').trim();
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(asString);
      const numeric = !isUuid && /^\d+$/.test(asString) ? Number(asString) : null;

      const payload: Record<string, any> = { access };
      if (numeric != null) {
        payload.collaborator_user_id = numeric;
      } else if (isUuid) {
        payload.collaborator_user_uuid = asString;
      } else {
        feedback(dispatch, 'error', 'Failed to grant access: missing user identifier');
        throw new Error('missing_user_identifier');
      }

      const response = await apis.post(`/projects/${projectUuid}/collaborators`, payload);
      if (response?.data && response.data.success === false) {
        const msg = response.data.error || 'Failed to grant access';
        feedback(dispatch, 'error', String(msg));
        throw new Error(String(msg));
      }
      feedback(dispatch, 'success', 'Access granted successfully');
      dispatch(fetchProjectCollaborators(projectUuid));
      return true;
    } catch (error: any) {
      // Only surface the generic toast if no more specific error was already shown.
      if (!error || (error.message !== 'missing_user_identifier' && !String(error.message || '').toLowerCase().includes('failed to grant access'))) {
        feedback(dispatch, 'error', 'Failed to grant access');
      }
      throw error;
    }
  };
};

export const removeProjectCollaborator = (projectUuid: string, collaboratorUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/collaborators/remove`, {
        collaborator_uuid: collaboratorUuid,
      });
      feedback(dispatch, 'success', 'Access removed');
      dispatch(fetchProjectCollaborators(projectUuid));
      return true;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to remove access');
      return false;
    }
  };
};

// ─── Dashboard ─────────────────────────────────────────────────

export const fetchProjectDashboard = (projectUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING_DASHBOARD, payload: true });
      const response = await apis.get(`/projects/${projectUuid}/dashboard`);
      dispatch({ type: PROJECTS_SET_DASHBOARD, payload: response.data });
      return response.data;
    } catch (error: any) {
      return null;
    } finally {
      dispatch({ type: PROJECTS_LOADING_DASHBOARD, payload: false });
    }
  };
};

// ─── Feed ──────────────────────────────────────────────────────

export const fetchProjectFeed = (projectUuid: string, page: number = 1, limit: number = 50) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING_FEED, payload: true });
      const response = await apis.get(`/projects/${projectUuid}/feed`, { params: { page, limit } });
      const actionType = page > 1 ? PROJECTS_APPEND_FEED : PROJECTS_SET_FEED;
      dispatch({ type: actionType, payload: {
        feed: response.data.feed || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        total_pages: response.data.total_pages || 0,
      }});
      return response.data;
    } catch (error: any) {
      return { feed: [], total: 0 };
    } finally {
      dispatch({ type: PROJECTS_LOADING_FEED, payload: false });
    }
  };
};

export const createFeedEvent = (projectUuid: string, message: string, eventType: string = 'post') => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/feed`, { message, event_type: eventType });
      if (response.data.success) {
        dispatch(fetchProjectFeed(projectUuid));
      }
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to post');
      return null;
    }
  };
};

export const addFeedComment = (projectUuid: string, eventId: number, message: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/feed/${eventId}/comments`, { message });
      if (response.data.success) {
        dispatch(fetchProjectFeed(projectUuid));
      }
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to add comment');
      return null;
    }
  };
};

// ─── Milestones ────────────────────────────────────────────────

export const fetchMilestones = (projectUuid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch({ type: PROJECTS_LOADING_MILESTONES, payload: true });
      const response = await apis.get(`/projects/${projectUuid}/milestones`);
      dispatch({ type: PROJECTS_SET_MILESTONES, payload: response.data.milestones || [] });
      return response.data.milestones;
    } catch (error: any) {
      return [];
    } finally {
      dispatch({ type: PROJECTS_LOADING_MILESTONES, payload: false });
    }
  };
};

export const createMilestone = (projectUuid: string, data: { title: string; description?: string; due_date?: string; status?: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/milestones`, data);
      feedback(dispatch, 'success', 'Milestone added');
      dispatch(fetchMilestones(projectUuid));
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to add milestone');
      return null;
    }
  };
};

export const updateMilestone = (projectUuid: string, milestoneId: number, data: any) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.put(`/projects/${projectUuid}/milestones/${milestoneId}`, data);
      dispatch(fetchMilestones(projectUuid));
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to update milestone');
      return null;
    }
  };
};

export const completeMilestone = (projectUuid: string, milestoneId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/milestones/${milestoneId}/complete`);
      feedback(dispatch, 'success', 'Milestone completed');
      dispatch(fetchMilestones(projectUuid));
      return true;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to complete milestone');
      return false;
    }
  };
};

export const deleteMilestone = (projectUuid: string, milestoneId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.delete(`/projects/${projectUuid}/milestones/${milestoneId}`);
      feedback(dispatch, 'success', 'Milestone removed');
      dispatch(fetchMilestones(projectUuid));
      return true;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to remove milestone');
      return false;
    }
  };
};

export const reorderMilestones = (projectUuid: string, milestoneIds: number[]) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/milestones/reorder`, { milestone_ids: milestoneIds });
      dispatch(fetchMilestones(projectUuid));
      return true;
    } catch (error: any) {
      return false;
    }
  };
};

// ─── Access Control ───────────────────────────────────────────

export const checkResourceAccess = (projectUuid: string, resourceType: string, resourceUuid: string) => {
  return async (_dispatch: AppDispatch) => {
    try {
      const response = await apis.get(`/projects/${projectUuid}/access-check`, {
        params: { resource_type: resourceType, resource_uuid: resourceUuid },
      });
      return response.data;
    } catch (error: any) {
      return null;
    }
  };
};

export const grantResourceAccess = (projectUuid: string, resourceType: string, resourceUuid: string, grants: { user_id: number; access: string }[]) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/${projectUuid}/grant-access`, {
        resource_type: resourceType,
        resource_uuid: resourceUuid,
        grants,
      });
      if (response.data.granted_count > 0) {
        feedback(dispatch, 'success', `Access granted to ${response.data.granted_count} member(s)`);
      }
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to grant access');
      return null;
    }
  };
};

export const requestResourceAccess = (projectUuid: string, resourceType: string, resourceUuid?: string, message?: string, linkId?: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const body: Record<string, unknown> = { resource_type: resourceType };
      if (resourceUuid) body.resource_uuid = resourceUuid;
      if (linkId !== undefined) body.link_id = linkId;
      if (message) body.message = message;
      const response = await apis.post(`/projects/${projectUuid}/request-access`, body);
      feedback(dispatch, 'success', 'Access request sent to the resource owner');
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to send access request');
      return null;
    }
  };
};

export const fetchAccessRequests = (resourceType: string, resourceUuid: string) => {
  return async (_dispatch: AppDispatch) => {
    try {
      const response = await apis.get('/projects/access-requests', {
        params: { resource_type: resourceType, resource_uuid: resourceUuid },
      });
      return response.data.requests || [];
    } catch (error: any) {
      return [];
    }
  };
};

export const respondToAccessRequest = (
  requestId: number,
  action: 'approve' | 'deny',
  accessLevel: string = 'view',
  // When true (default), approved grants are tagged source='project' so they
  // cascade-revoke with the Project link / membership. Passing false issues a
  // permanent direct grant instead. Matches the checkbox in PendingAccessRequests.
  tiedToProject: boolean = true,
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await apis.post(`/projects/access-requests/${requestId}/respond`, {
        action,
        access_level: accessLevel,
        tied_to_project: tiedToProject,
      });
      feedback(dispatch, 'success', action === 'approve' ? 'Access granted' : 'Request denied');
      return response.data;
    } catch (error: any) {
      feedback(dispatch, 'error', 'Failed to respond to access request');
      return null;
    }
  };
};

export const fetchLinkableProjects = () => {
  return async (_dispatch: AppDispatch) => {
    try {
      const response = await apis.get('/projects/linkable');
      return response.data.projects || [];
    } catch (error: any) {
      return [];
    }
  };
};

export const fetchProjectsByResource = (resourceType: string, resourceUuid: string) => {
  return async (_dispatch: AppDispatch) => {
    try {
      const response = await apis.get('/projects/by-resource', {
        params: { resource_type: resourceType, resource_uuid: resourceUuid },
      });
      return response.data.projects || [];
    } catch (error: any) {
      return [];
    }
  };
};

// ─── Audit Log ─────────────────────────────────────────────────

export const fetchProjectAuditLog = (projectUuid: string, page: number = 1, limit: number = 50) => {
  return async (_dispatch: AppDispatch) => {
    try {
      const response = await apis.get(`/audit-logs/entity/Project/${projectUuid}`, {
        params: { page, per_page: limit },
      });
      return response.data;
    } catch (error: any) {
      return { entries: [], pagination: { has_next: false, total_items: 0 } };
    }
  };
};

// ─── Clear Detail ──────────────────────────────────────────────

export const updateProjectCollaboratorAccess = (projectUuid: string, collaboratorUuid: string, access: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await apis.post(`/projects/${projectUuid}/collaborators/update-access`, {
        collaborator_uuid: collaboratorUuid,
        access,
      });
      return true;
    } catch (error: any) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: 'Failed to update access level', duration: 3000 }));
      return false;
    }
  };
};

export const clearProjectDetail = () => {
  return (dispatch: AppDispatch) => {
    dispatch({ type: PROJECTS_SET_DETAIL, payload: null });
    dispatch({ type: PROJECTS_SET_RESOURCES, payload: null });
    dispatch({ type: PROJECTS_SET_COLLABORATORS, payload: [] });
  };
};
